import React, { useState } from 'react';
import { Edit2, Check, X } from 'lucide-react';

interface InfoCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  editable?: boolean;
  onSave?: (data: any) => void;
}

export const InfoCard: React.FC<InfoCardProps> = ({ 
  title, 
  icon, 
  children,
  editable = false,
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});

  const handleSave = () => {
    if (onSave) {
      onSave(editedValues);
    }
    setIsEditing(false);
    setEditedValues({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedValues({});
  };

  const handleFieldChange = (label: string, value: string) => {
    setEditedValues(prev => ({ ...prev, [label]: value }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="text-gray-600">{icon}</div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        {editable && (
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="text-green-600 hover:text-green-700 transition-colors"
                  title="Save"
                >
                  <Check size={18} />
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  title="Cancel"
                >
                  <X size={18} />
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Edit"
              >
                <Edit2 size={16} />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="space-y-2">
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            if (child.type === InfoRow) {
              const childProps = child.props as any;
              return React.cloneElement(child, { 
                isEditing,
                onChange: (value: string) => handleFieldChange(childProps.label, value)
              } as any);
            }
          }
          return child;
        })}
      </div>
    </div>
  );
};

interface InfoRowProps {
  label: string;
  value?: string | boolean;
  type?: 'text' | 'boolean';
  isEditing?: boolean;
  onChange?: (value: string) => void;
}

export const InfoRow: React.FC<InfoRowProps> = ({ 
  label, 
  value, 
  type = 'text',
  isEditing = false,
  onChange
}) => {
  const [localValue, setLocalValue] = useState<string>(() => {
    // Initialize with the display value
    return type === 'boolean' 
      ? (value ? 'Yes' : 'No')
      : (value?.toString() || '');
  });
  
  React.useEffect(() => {
    const displayValue = type === 'boolean' 
      ? (value ? 'Yes' : 'No')
      : (value?.toString() || '');
    setLocalValue(displayValue);
  }, [value, type]);
  
  // Return null AFTER all hooks
  if (!value && value !== false && !isEditing) return null;
  
  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    onChange?.(newValue);
  };
  
  const displayValue = type === 'boolean' 
    ? (value ? 'Yes' : 'No')
    : (value?.toString() || '');
  
  return (
    <div className="flex flex-col gap-1 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      <div className="flex">
        {isEditing ? (
          type === 'boolean' ? (
            <select
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
              className="text-sm text-gray-900 font-medium bg-blue-50 pl-2 pr-8 py-1 focus:outline-none focus:bg-blue-100 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMS41TDYgNi41TDExIDEuNSIgc3Ryb2tlPSIjNkI3MjgwIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+PC9zdmc+')] bg-[length:12px] bg-[right_0.5rem_center] bg-no-repeat"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          ) : (
            <input
              type="text"
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
              className="text-sm text-gray-900 font-medium bg-blue-50 px-2 py-1 focus:outline-none focus:bg-blue-100 w-full"
            />
          )
        ) : (
          <span className="text-sm text-gray-900 font-medium px-2 py-1">{displayValue}</span>
        )}
      </div>
    </div>
  );
};
